/**
 * Global filters context provider - React state management
 */

import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import type { GlobalFilters, GlobalFiltersContextType } from './types';
import {
  getDefaultFilters,
  loadFiltersFromStorage,
  saveFiltersToStorage,
  checkHasActiveFilters,
  mergeFilters
} from './operations';

const GlobalFiltersContext = createContext<GlobalFiltersContextType | undefined>(undefined);

interface GlobalFiltersProviderProps {
  children: ReactNode;
}

export const GlobalFiltersProvider = ({ children }: GlobalFiltersProviderProps) => {
  const [filters, setFilters] = useState<GlobalFilters>(loadFiltersFromStorage);

  // Persist filters to localStorage
  useEffect(() => {
    saveFiltersToStorage(filters);
  }, [filters]);

  const updateFilters = (newFilters: Partial<GlobalFilters>) => {
    setFilters(prev => mergeFilters(prev, newFilters));
  };

  const clearFilters = () => {
    setFilters(getDefaultFilters());
  };

  const hasActiveFilters = () => {
    return checkHasActiveFilters(filters);
  };

  const value = useMemo(
    () => ({
      filters,
      updateFilters,
      clearFilters,
      hasActiveFilters
    }),
    [filters]
  );

  return (
    <GlobalFiltersContext.Provider value={value}>
      {children}
    </GlobalFiltersContext.Provider>
  );
};

export const useGlobalFilters = () => {
  const context = useContext(GlobalFiltersContext);
  if (context === undefined) {
    throw new Error('useGlobalFilters must be used within a GlobalFiltersProvider');
  }
  return context;
};

export { GlobalFiltersContext };
